import React, { useEffect, useRef, useState } from 'react';
import { Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { companyAPI } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import './CompanySettings.css';

const ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp';
const MAX_BYTES = 2 * 1024 * 1024;

const CompanySettings = () => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [cacheBuster, setCacheBuster] = useState(Date.now());
  const inputRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await companyAPI.get();
      setCompany(data);
    } catch {
      toast.error('Firmendaten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error('Datei zu groß (max. 2 MB)');
      return;
    }
    if (!ACCEPT.split(',').includes(file.type)) {
      toast.error('Nur JPG, PNG oder WEBP erlaubt');
      return;
    }
    setPreviewFile(file);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!previewFile) return;
    try {
      setUploading(true);
      await companyAPI.uploadLogo(previewFile);
      toast.success('Logo gespeichert');
      setPreview(null);
      setPreviewFile(null);
      if (inputRef.current) inputRef.current.value = '';
      setCacheBuster(Date.now());
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Logo wirklich entfernen?')) return;
    try {
      setUploading(true);
      await companyAPI.deleteLogo();
      toast.success('Logo entfernt');
      setCacheBuster(Date.now());
      await load();
    } catch {
      toast.error('Entfernen fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="large" />
      </div>
    );
  }
  if (!company) return null;

  const currentLogoUrl = company.logo_url
    ? `${companyAPI.logoUrl()}?t=${cacheBuster}`
    : null;

  return (
    <div>
      <div className="content-header">
        <div>
          <h1>Einstellungen</h1>
          <p>Firmendaten und Logo für Rechnungen &amp; Angebote</p>
        </div>
      </div>

      <div className="content-body">
        <div className="card mb-6">
          <div className="card-header"><h2>Firmen-Logo</h2></div>
          <div className="card-body">
            <div className="logo-settings">
              <div className="logo-preview">
                {preview ? (
                  <img src={preview} alt="Vorschau" />
                ) : currentLogoUrl ? (
                  <img src={currentLogoUrl} alt="Firmenlogo" />
                ) : (
                  <div className="logo-placeholder">
                    <ImageIcon size={40} />
                    <span>Noch kein Logo</span>
                  </div>
                )}
              </div>
              <div className="logo-actions">
                <p className="text-muted mb-2">
                  JPG, PNG oder WEBP · max. 2&nbsp;MB. Das Logo erscheint auf
                  allen PDFs, im Seitenmenü und auf der Login-Seite.
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPT}
                  onChange={onPick}
                  className="form-input"
                />
                <div className="flex gap-2 mt-3">
                  <button
                    className="btn btn-primary"
                    onClick={handleUpload}
                    disabled={!previewFile || uploading}
                  >
                    <Upload size={14} />
                    Speichern
                  </button>
                  {company.logo_exists && (
                    <button
                      className="btn btn-danger"
                      onClick={handleDelete}
                      disabled={uploading}
                    >
                      <Trash2 size={14} />
                      Entfernen
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h2>Firmenangaben</h2></div>
          <div className="card-body">
            <p className="text-muted mb-3">
              Diese Daten werden aus <code>company.env</code> geladen und erscheinen
              auf allen PDFs sowie in den Pflichtangaben (§14 UStG).
            </p>
            <dl className="company-meta">
              <dt>Firma</dt><dd>{company.name}</dd>
              <dt>Inhaber</dt><dd>{company.owner}</dd>
              <dt>Adresse</dt>
              <dd>
                {company.address_line1}
                {company.address_line2 ? <><br/>{company.address_line2}</> : null}
                <br/>{company.postal_code} {company.city}
                <br/>{company.country}
              </dd>
              <dt>Telefon</dt><dd>{company.phone || '—'}</dd>
              <dt>E-Mail</dt><dd>{company.email || '—'}</dd>
              <dt>Web</dt><dd>{company.website || '—'}</dd>
              <dt>Steuernummer</dt><dd>{company.tax_number || '—'}</dd>
              <dt>USt-IdNr.</dt><dd>{company.vat_id || '—'}</dd>
              <dt>Finanzamt</dt><dd>{company.tax_office || '—'}</dd>
              <dt>Kleinunternehmer</dt>
              <dd>{company.small_business ? 'Ja (§19 UStG)' : 'Nein'}</dd>
              <dt>IBAN</dt><dd>{company.bank_iban || '—'}</dd>
              <dt>BIC</dt><dd>{company.bank_bic || '—'}</dd>
              <dt>Bank</dt><dd>{company.bank_name || '—'}</dd>
              <dt>Zahlungsziel</dt><dd>{company.payment_term_days} Tage</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;
